import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";
import { useClient } from "../context/ClientContext";
import GooeySVG from "../shared/components/folderSVG";

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  cpassword: string;
  user_category_id: number;
  token: string;
}

// Example SimpleSlider reuse
const SimpleSlider = () => {
  const images = [
    "/assets/images/slider1.png",
    "/assets/images/slider1.png",
    "/assets/images/slider1.png",
  ];
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative w-full overflow-hidden">
      <img
        src={images[currentIndex]}
        alt="Slide"
        className="w-full h-fit items-center justify-center"
      />

      <div className="absolute bottom-2 left-0 right-0 flex justify-center space-x-2">
        <button
          onClick={handlePrev}
          className="px-3 py-1 bg-white text-gray-50 rounded shadow hover:bg-primary-200"
        >
          Prev
        </button>
        <button
          onClick={handleNext}
          className="px-3 py-1 bg-white text-gray-50 rounded shadow hover:bg-primary-200"
        >
          Next
        </button>
      </div>
    </div>
  );
};

const registerUserAPI = async (client: any, data: RegisterFormData) => {
  try {
    return await client.post("/users/create", data);
  } catch (error: any) {
    throw error;
  }
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const notification = useNotification();
  const client = useClient("AUTH_SERVICE");

  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormData>({
    defaultValues: {
      user_category_id: 2, // default to External User
      token: "my-default-token", // or generate a random token here if needed
    },
  });

  const [loading, setLoading] = useState(false);
  const [hidePassword, setHidePassword] = useState(true);

  const onSubmit = async (data: RegisterFormData) => {
    // Check password length
    if (data.password.length < 8) {
      notification.error("Password must be at least 8 characters long.");
      return;
    }
    // Check confirm password
    if (data.password !== data.cpassword) {
      notification.error("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      // Call your service or direct API
      const response = await registerUserAPI(client, data);

      if (response?.message?.toLowerCase().includes("already exists")) {
        notification.error("User already exists with that email.");
      } else if (
        response?.message?.toLowerCase().includes("created sucessfully")
      ) {
        notification.success(
          "User created successfully! Please verify your email address"
        );
        // Typically navigate to login or wherever you prefer
        navigate("/login");
      } else {
        // Fallback: handle unexpected statuses
        notification.error("Registration failed.");
      }
    } catch (error: any) {
      notification.error(
        error.message || "An error occurred during registration."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-full flex items-center justify-center relative">
      {/* Optional Gooey filter */}
      <GooeySVG />

      <div className="container mx-auto p-4 flex items-center justify-center">
        {/* Two side-by-side "cards," each constrained by max-w-lg */}
        <div className="flex space-x-0 lg:space-x-4">
          {/* LEFT COLUMN - Form */}
          <div className="flex-none bg-white rounded-lg shadow-lg overflow-hidden max-w-lg p-8">
            <div className="text-center mb-8">
              <a href="https://www.serceaproject.org">
                <img
                  className="mx-auto w-24 mb-3"
                  src="/assets/images/sercea-2.png"
                  alt="Sercea Project Logo"
                  title="Sercea Project"
                />
              </a>
              <h4 className="text-xl font-extrabold text-primary-600 tracking-wider leading-tight">
                Strengthening Ethics and Responsible Conduct of Clinical Trial
                in East and Sub-Sahara Africa
              </h4>
            </div>

            {/* Register Form */}
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <p className="mb-4 text-base text-gray-700">
                Create your account
              </p>

              {/* Name Input */}
              <div className="mb-4">
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  placeholder="Please enter your full name"
                  className="mt-1 w-full border border-gray-300 rounded-md shadow-sm
                             focus:ring-primary-600 focus:border-primary-600 p-2"
                  {...register("name", { required: "Name is required" })}
                />
                {errors.name && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Email Input */}
              <div className="mb-4">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  placeholder="Please enter valid email address"
                  className="mt-1 w-full border border-gray-300 rounded-md shadow-sm
                             focus:ring-primary-600 focus:border-primary-600 p-2"
                  {...register("email", { required: "Email is required" })}
                />
                {errors.email && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password Input */}
              <div className="mb-4">
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    type={hidePassword ? "password" : "text"}
                    id="password"
                    placeholder="Please enter your password"
                    className="mt-1 w-full border border-gray-300 rounded-md shadow-sm
                               focus:ring-primary-600 focus:border-primary-600 p-2"
                    {...register("password", {
                      required: "Password is required",
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setHidePassword(!hidePassword)}
                    className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-600 hover:text-gray-800"
                  >
                    <span className="material-icons text-base">
                      {hidePassword ? "visibility_off" : "visibility"}
                    </span>
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* CPassword Input */}
              <div className="mb-4">
                <label
                  htmlFor="cpassword"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={hidePassword ? "password" : "text"}
                    id="cpassword"
                    placeholder="Repeat your password"
                    className="mt-1 w-full border border-gray-300 rounded-md shadow-sm
                               focus:ring-primary-600 focus:border-primary-600 p-2"
                    {...register("cpassword", {
                      required: "Confirmation Password is required",
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setHidePassword(!hidePassword)}
                    className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-600 hover:text-gray-800"
                  >
                    <span className="material-icons text-base">
                      {hidePassword ? "visibility_off" : "visibility"}
                    </span>
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Hidden or default fields: user_category_id & token */}
              <input
                type="hidden"
                {...register("user_category_id", {
                  valueAsNumber: true,
                })}
              />
              <input type="hidden" {...register("token")} />

              {/* Submit Button */}
              <div className="mb-4 pt-2 text-center">
                <button
                  type="submit"
                  disabled={loading}
                  className={`inline-block w-full rounded px-6 py-2 text-xs font-medium uppercase leading-normal text-white
                              shadow-md transition duration-150 ease-in-out
                              bg-primary-600 hover:bg-primary-700 
                              disabled:bg-gray-400 disabled:cursor-not-allowed`}
                >
                  {loading ? "Registering..." : "Register"}
                </button>
              </div>

              {/* Switch to Login link */}
              <div className="flex items-center justify-center">
                <p className="mb-0 mr-2 text-sm text-gray-700">
                  Already have an account?
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className={`inline-block rounded border-2 border-primary-600 px-4 py-1 text-xs font-medium uppercase leading-normal text-primary-600
                              hover:bg-primary-50 focus:outline-none 
                              transition duration-150 ease-in-out`}
                >
                  Login
                </button>
              </div>
            </form>
          </div>

          {/* RIGHT COLUMN - Slider */}
          <div className="hidden lg:flex flex-none max-w-lg bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="w-full bg-gradient-to-r from-primary-600 via-pink-500 to-purple-500 p-6">
              <SimpleSlider />
              <div className="p-6 text-center bg-gray-100 w-full">
                <p className="text-sm text-gray-700 font-medium leading-relaxed">
                  <span className="font-semibold">
                    The Open Data Access Platform for Clinical Studies is a
                    web-based system designed to enhance data portability,
                    knowledge sharing, and evidence generation in clinical
                    research.
                  </span>
                  <span className="font-semibold">
                    {" "}
                    It provides researchers, policymakers, and institutions with
                    transparent and accessible data on clinical studies.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegisterPage;
